import { useMemo } from 'react';
import { ForecastModel, FORECAST_MODELS } from '@app/common/static-data/models';
import { useTimezone } from '@app/contexts/useTimezone';
import { LoadingSpinner } from '@app/components/common/LoadingSpinner';

import { PlottableForecast, ModelId, type WeatherCondition } from '../types';
import { useMultiModelWeatherSummary } from '../hooks/useWeatherSummary';
import type { WeatherSummarySlot } from '../utils/weatherSummaryUtils';
import { determineTimeOfDay, getConditionDescription } from '../utils/weatherConditionUtils';
import { WeatherSummaryHeader } from './WeatherSummaryHeader';
import { WeatherSummaryFooter } from './WeatherSummaryFooter';
import { WeatherSummaryCell, WeatherSummaryCellEmpty } from './WeatherSummaryCell';
import { WeatherSummaryTimeHeader } from './WeatherSummaryTimeHeader';
import { WeatherSummaryModelColumn } from './WeatherSummaryModelColumn';
// model visibility removed per UX feedback

interface WeatherSummaryViewProps {
  forecastData: Record<ModelId, PlottableForecast>;
  selectedModels: ForecastModel[];
  location: { lat: number; lon: number; name: string };
  customModelColors: Record<string, string>;
  endHour: number;
}

export function WeatherSummaryView({
  forecastData,
  selectedModels,
  location,
  customModelColors,
  endHour,
}: WeatherSummaryViewProps) {
  const { formatInUserTimezone } = useTimezone();

  // Calculate max slots based on endHour from sidebar (3-hour intervals)
  const maxSlots = Math.ceil(endHour / 3);

  // Get weather summary for all selected models
  const modelSummaries = useMultiModelWeatherSummary(forecastData, selectedModels, {
    timeSlotConfig: { intervalHours: 3, maxSlots },
  });

  // Check if we have any data
  const hasAnyData = Object.values(modelSummaries).some((summary) => summary.hasData);

  // Extract city name from full location
  const cityName = useMemo(() => {
    return location.name.split(',')[0].trim();
  }, [location.name]);

  // Format time for display
  const formatTime = (date: Date) => {
    return formatInUserTimezone(date, 'h:mm a');
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return formatInUserTimezone(date, 'EEE d');
  };

  // Format detailed time for tooltip
  const formatTooltipTime = (date: Date) => {
    return formatInUserTimezone(date, 'EEE, MMM d • h:mm a');
  };

  // Check if date is different from previous
  const isDifferentDay = (currentDate: Date, previousDate: Date | null) => {
    if (!previousDate) return true;
    return (
      formatInUserTimezone(currentDate, 'yyyy-MM-dd') !==
      formatInUserTimezone(previousDate, 'yyyy-MM-dd')
    );
  };

  // Find the closest slot in a model for the given header time
  const findClosestSlot = (
    slots: WeatherSummarySlot[],
    targetTime: Date,
    toleranceMinutes: number = 90,
  ): WeatherSummarySlot | null => {
    if (!slots || slots.length === 0) return null;
    let closest: WeatherSummarySlot | null = null;
    let minDiff = Number.POSITIVE_INFINITY;
    const targetMs = targetTime.getTime();
    for (const slot of slots) {
      const diff = Math.abs(slot.time.getTime() - targetMs);
      if (diff < minDiff) {
        minDiff = diff;
        closest = slot;
      }
    }
    return minDiff <= toleranceMinutes * 60 * 1000 ? closest : null;
  };

  // Get model display info
  const getModelInfo = (modelId: ModelId) => {
    const model = FORECAST_MODELS.find((m) => m.id === modelId);
    return {
      label: model?.label || modelId,
      color: customModelColors[modelId] || '#EC4899', // default to pink
    };
  };

  // Loading state
  if (!hasAnyData) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] space-y-4">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600 dark:text-gray-400">Loading weather summary...</p>
      </div>
    );
  }

  // Filter out models with no data and sort by model order
  const modelsWithData = selectedModels.filter((modelId) => modelSummaries[modelId]?.hasData);

  return (
    <div className="flex flex-col h-full w-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <WeatherSummaryHeader
        cityName={cityName}
        currentDate={formatInUserTimezone(new Date(), 'EEE, MMM d, yyyy')}
        endHour={endHour}
        modelsWithData={modelsWithData}
        getModelInfo={getModelInfo}
      />

      {/* Weather Timeline - Grid Comparison (Time x Models) */}
      <div className="flex-1 overflow-auto relative">
        <div className="inline-flex flex-col min-w-full">
          {/* Get all unique time slots from the first model with data */}
          {(() => {
            const firstModelSummary = modelSummaries[modelsWithData[0]];
            if (!firstModelSummary?.hasData) return null;

            const timeSlots = firstModelSummary.slots.map((slot) => slot.time);

            // Compute consensus across visible models for each time column
            const consensusForTime = (colTime: Date) => {
              const temps: number[] = [];
              const precs: number[] = [];
              const winds: number[] = [];
              const conditions: Record<string, number> = {};

              modelsWithData.forEach((modelId) => {
                const s = findClosestSlot(
                  modelSummaries[modelId].slots as unknown as WeatherSummarySlot[],
                  colTime,
                );
                if (!s) return;
                temps.push(s.weatherState.temperature);
                precs.push(s.weatherState.precipitation);
                winds.push(s.weatherState.windSpeed);
                const c = s.weatherState.condition;
                conditions[c] = (conditions[c] || 0) + 1;
              });

              if (temps.length === 0) return null;

              const avg = (arr: number[]) =>
                Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
              const predominant = Object.entries(conditions).sort((a, b) => b[1] - a[1])[0]?.[0];

              return {
                temperature: { min: Math.min(...temps), max: Math.max(...temps), avg: avg(temps) },
                precipitation: {
                  avg: Math.round((precs.reduce((a, b) => a + b, 0) / precs.length) * 10) / 10,
                },
                wind: { avg: avg(winds) },
                predominant,
              } as const;
            };

            return (
              <div className="flex relative">
                {/* Left column: Model names */}
                <WeatherSummaryModelColumn
                  modelsWithData={modelsWithData}
                  modelSummaries={modelSummaries}
                  getModelInfo={getModelInfo}
                  showConsensusLabel
                />

                {/* Right side: Scrollable time columns */}
                <div className="flex relative">
                  {timeSlots.map((time, timeIndex) => {
                    const previousTime = timeIndex > 0 ? timeSlots[timeIndex - 1] : null;
                    const showDayLabel = isDifferentDay(time, previousTime);

                    return (
                      <div key={timeIndex} className="flex-shrink-0">
                        {/* Time header - sticky on vertical scroll */}
                        <div className="sticky top-0 z-30">
                          <WeatherSummaryTimeHeader
                            time={time}
                            showDayLabel={showDayLabel}
                            formatTime={formatTime}
                            formatDate={formatDate}
                          />
                        </div>

                        {/* Consensus summary row */}
                        {(() => {
                          const c = consensusForTime(time);
                          if (!c) return null;
                          const readable = c.predominant
                            ? getConditionDescription(c.predominant as WeatherCondition)
                            : '';
                          return (
                            <div className="h-10 w-20 border-b border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center">
                              <div className="text-[11px] leading-tight text-gray-700 dark:text-gray-300 text-center">
                                <div className="font-semibold">{c.temperature.avg}°</div>
                                <div className="opacity-70">{readable}</div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Model forecasts for this time */}
                        {modelsWithData.map((modelId) => {
                          const summary = modelSummaries[modelId];
                          const modelInfo = getModelInfo(modelId);
                          const slot = findClosestSlot(
                            summary.slots as unknown as WeatherSummarySlot[],
                            time,
                          );

                          if (!slot) {
                            return (
                              <WeatherSummaryCellEmpty key={modelId} showDayLabel={showDayLabel} />
                            );
                          }

                          // Use the column header time to determine day/night for consistent icons across models
                          const headerHour = parseInt(formatInUserTimezone(time, 'H'), 10);
                          const headerTimeOfDay = determineTimeOfDay(headerHour);
                          const displayState = { ...slot.weatherState, timeOfDay: headerTimeOfDay };

                          return (
                            <WeatherSummaryCell
                              key={modelId}
                              weatherState={displayState}
                              modelInfo={modelInfo}
                              showDayLabel={showDayLabel}
                              tooltipTime={formatTooltipTime(time)}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Footer */}
      <WeatherSummaryFooter maxSlots={maxSlots} />
    </div>
  );
}
